import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo
} from 'react';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import { useQuery } from '@tanstack/react-query';
import { BehLayoutSkeleton } from '../../layout/BehLayoutSkeleton';
import { govosClient } from 'api/govos-api';
import { BehApiRoutes, behClient } from '../../api/beh-api';
import { getLogoutUrl, isBearerTokenAuthEnabled } from './auth';
import keycloak from './keycloak';

export interface Profile {
  firstName: string;
  lastName: string;
  id: string;
  name: string;
  email: string;
  given_name: string;
  tenant?: string;
  groups: string[];
  roles: string[];
  tenantRoles: string[];
}

export interface AuthContextValue {
  profile?: Profile;
  error?: Error | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  // Only available when bearer token auth is disabled (Keycloak)
  login?: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useProfile() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useProfile must be used within AuthProvider');
  }

  const inGroup = useCallback(
    (group: string) => context.profile?.groups?.includes(group) ?? false,
    [context.profile]
  );

  const hasRole = useCallback(
    (requestedRole: string) => {
      return (
        context.profile?.groups?.find((role) =>
          role.includes(requestedRole)
        ) !== undefined
      );
    },
    [context.profile?.groups]
  );

  const isTenantAdmin = (theProfile: Profile) => {
    return isBearerTokenAuthEnabled()
      ? hasRole('/Administration')
      : theProfile.tenantRoles.includes('/Administration');
  };

  return { ...context, isTenantAdmin, hasRole, inGroup };
}

function ConfiguredKeycloakProvider({ children }: { children: ReactNode }) {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      LoadingComponent={<BehLayoutSkeleton />}
      initOptions={{ checkLoginIframe: false }}
      onTokens={(token) => {
        if (token.token) {
          behClient.defaults.headers.common.Authorization = `Bearer ${token.token}`;
          govosClient.defaults.headers.common.Authorization = `Bearer ${token.token}`;
        } else {
          delete behClient.defaults.headers.common.Authorization;
          delete govosClient.defaults.headers.common.Authorization;
        }
      }}
    >
      {children}
    </ReactKeycloakProvider>
  );
}

function BearerProfileContent({ children }: { children: ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { keycloak, initialized } = useKeycloak();

  const { data, error, isLoading } = useQuery({
    queryKey: [BehApiRoutes.profile()],
    queryFn: () => keycloak.loadUserProfile(),
    enabled: keycloak.authenticated
  });

  const value = useMemo(
    () => ({
      profile: {
        email: keycloak.tokenParsed?.email || '',
        firstName: keycloak.tokenParsed?.given_name || '',
        lastName: keycloak.tokenParsed?.family_name || '',
        id: keycloak.profile?.id || '',
        name: `${keycloak.tokenParsed?.given_name} ${keycloak.tokenParsed?.family_name}`,
        groups: keycloak.tokenParsed?.groups || [],
        roles: [],
        tenantRoles: [],
        given_name: keycloak.tokenParsed?.given_name || '',
        family_name: keycloak.tokenParsed?.family_name || ''
      } as Profile,
      error,
      login: () => keycloak.login(),
      logout: () => keycloak.logout(),
      isLoading,
      isAuthenticated: Boolean(keycloak.authenticated)
    }),
    [
      data,
      error,
      isLoading,
      keycloak.authenticated,
      keycloak.login,
      keycloak.logout
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {initialized ? children : <BehLayoutSkeleton />}
    </AuthContext.Provider>
  );
}

export function BearerProfileProvider({ children }: { children: ReactNode }) {
  return (
    <ConfiguredKeycloakProvider>
      <BearerProfileContent>{children}</BearerProfileContent>
    </ConfiguredKeycloakProvider>
  );
}

interface InternalProfile {
  id: string;
  groups: string[];
  roles: string[];
  email: string;
  name: string;
  given_name: string;
  family_name: string;
}

export function CookieProfileProvider({ children }: { children: ReactNode }) {
  const { data, error, isLoading } = useQuery<InternalProfile | undefined>({
    queryKey: [BehApiRoutes.profile()],
    queryFn: () =>
      behClient.get<InternalProfile>(BehApiRoutes.profile()).then((r) => r.data)
  });

  const logout = useCallback(() => {
    const logoutUrl = getLogoutUrl();

    if (!logoutUrl) {
      return;
    }

    window.location.assign(logoutUrl);
  }, []);

  const value = useMemo(() => {
    const profile: Profile | undefined = data
      ? {
          firstName: data.given_name,
          lastName: data.family_name,
          id: data.id,
          name: data.name,
          tenant: '',
          email: data.email,
          groups: data.groups,
          roles: data.roles,
          tenantRoles: [],
          given_name: data.given_name
        }
      : undefined;

    return {
      profile,
      error,
      isAuthenticated: true,
      isLoading,
      logout
    };
  }, [data, error, logout, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isBearerTokenAuthEnabled()) {
    return <BearerProfileProvider>{children}</BearerProfileProvider>;
  }

  return <CookieProfileProvider>{children}</CookieProfileProvider>;
}
